import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/editPri.svg";
import { ReactComponent as LinkIcon } from "../../../myfin_v2/assets/icons/Link.svg";
import { ReactComponent as FormulaIcon } from "../../../myfin_v2/assets/icons/formula.svg";
import { ReactComponent as DelIcon } from "../../../myfin_v2/assets/icons/delete_red.svg";
import { ReactComponent as PlusIcon} from "../../../myfin_v2/assets/icons/AddPlus.svg";
import commoncss from "../../css/common.module.css";
import libcss from "../../css/library.module.css";
import linkedIcon from "../../assets/icons/linked.png";
import closeIcon from "../../assets/icons/custModeLCloseIcon.svg";
import MenuIcon from "../../assets/icons/Menu.png";
import BoldIcon from "../../assets/icons/BoldIcon.png";

/**
 * Props:
 *  value: { tableTitle, tableRow:[{name,lineType}], tableColumn:[{name,lineType}] }
 *  onChange(nextValue)
 *  forceOpen?: boolean
 *  onAfterOpen?: () => void
 *  disabled?: boolean
 */

const formatIndianNumber = (val) => {
  if (val === "" || val === null || val === undefined) return val;
  const num = Number(val);
  if (isNaN(num)) return val;
  if (num < 0) {
    return "(" + (-1 * num).toLocaleString("en-IN") + ")";
  }
  return num.toLocaleString("en-IN");
};

const isNumeric = (val) => {
  if (val === "" || val === null || val === undefined) return false;
  if (typeof val === "string" && val.trim() === "") return false;
  const stripped = String(val).replace(/,/g, "").trim();
  if (stripped === "") return false;
  return !isNaN(Number(stripped));
};

const DragHandleIcon = ({ disabled }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      cursor: disabled ? "default" : "grab",
      opacity: disabled ? 0.3 : 0.7,
    }}
  >
    <circle cx="9" cy="5" r="1.5" fill="currentColor" />
    <circle cx="15" cy="5" r="1.5" fill="currentColor" />
    <circle cx="9" cy="12" r="1.5" fill="currentColor" />
    <circle cx="15" cy="12" r="1.5" fill="currentColor" />
    <circle cx="9" cy="19" r="1.5" fill="currentColor" />
    <circle cx="15" cy="19" r="1.5" fill="currentColor" />
  </svg>
);

export default function TemplateTableGenerator({
  value,
  onChange,
  forceOpen = false,
  onAfterOpen,
  disabled = false,
  allowReorder = false,
  isDisclosureTable = false,
  onCellLinkClick,
  linkedCells,
  tableId,
  isSocieTable = false,
  isCashflowTable = false,
  hideHeader = false,
  onCancel,
  getRowPermissions,
  onDelete,
  onDeleteRow,
  onAddRow,
  allowAddRow = false,
  onCellBlur,
  onToggleRowBold,
  hideRowBoldIcon = false,
  onSaveStructure,
}) {
  const totalRows = 20;
  const totalCols = 20;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoverRows, setHoverRows] = useState(0);
  const [hoverCols, setHoverCols] = useState(0);
  const [selectedRows, setSelectedRows] = useState(0);
  const [selectedCols, setSelectedCols] = useState(0);
  const [tableName, setTableName] = useState(value?.tableTitle || "");
  const [generated, setGenerated] = useState(false);
  const [cellValues, setCellValues] = useState({});
  const [focusedCell, setFocusedCell] = useState(null);
  const [enableCellEntries, setEnableCellEntries] = useState(true);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [hoveredRowIdx, setHoveredRowIdx] = useState(null);
  const [rowMetadata, setRowMetadata] = useState([]);

  // Local state for Modal to prevent "automatic creation/update" on change
  const [tempTableName, setTempTableName] = useState("");
  const [tempRows, setTempRows] = useState(0);
  const [tempCols, setTempCols] = useState(0);
  const [tempEnableCells, setTempEnableCells] = useState(true);

  const modalPrefilledRef = useRef(false);
  const skipPrefillOnCloseRef = useRef(false);
  const lastSentPayloadRef = useRef(null);
  const lastChangedRowRef = useRef(null);
  const ignoreValueSyncRef = useRef(false);
  const justClosedModalRef = useRef(false);

  const makeId = (prefix, index) => `${prefix}${index + 1}`;

  const openModal = () => {
    if (disabled) return;

    let currentTableName = tableName;
    let currentRows = selectedRows;
    let currentCols = selectedCols;
    let currentEnableCells = enableCellEntries;

    // Prefill state if not already done
    if (!modalPrefilledRef.current && !(Array.isArray(value?.table) && value.table.length > 0)) {
      applyValueToState(value);
      modalPrefilledRef.current = true;
      
      const incoming = value;
      if (incoming) {
        const cellEntriesEnabled =
          incoming.hasCellData !== undefined
            ? incoming.hasCellData
            : (incoming.tableType === "content" || (Array.isArray(incoming.table) && incoming.table.length > 0));
        currentEnableCells = cellEntriesEnabled;

        if (Array.isArray(incoming.table) && incoming.table.length > 0) {
          currentRows = incoming.table.length + 1;
          const columns = Object.keys(incoming.table[0] || {}).filter(c => {
            const lower = String(c.name || c || "").toLowerCase().trim();
            return (
              lower !== "linetype" && 
              lower !== "line type" && 
              lower !== "line-type" && 
              lower !== "_id" && 
              lower !== "createdviaaddlineitem" &&
              lower !== "cashflowid" &&
              lower !== "isedited" &&
              lower !== "isbold" &&
              lower !== "reference" &&
              lower !== "references" &&
              lower !== "lineitem" &&
              lower !== "formulatype" &&
              (!(isCashflowTable || isSocieTable) || (lower !== "issubrow" && lower !== "isnew" && lower !== "isnewrow")) &&
              lower !== "groupingid" && lower !== "grouping id" && (!isCashflowTable || lower !== "formula")
            );
          });
          currentCols = columns.length;
          currentTableName = incoming.tableTitle || "";
        } else {
          const incomingRowsArr = incoming.tableRow && incoming.tableRow.length > 0 
            ? incoming.tableRow 
            : (isDisclosureTable ? [{ name: "", isNewRow: true, lineType: "line item" }] : []);
          currentRows = incomingRowsArr.length + 1;
          const filteredCols = (incoming.tableColumn || []).filter(c => {
            const lower = String(c.name || "").toLowerCase().trim();
            return (
              lower !== "linetype" && 
              lower !== "line type" && 
              lower !== "line-type" &&
              lower !== "isbold" &&
              lower !== "reference" &&
              lower !== "references" &&
              lower !== "lineitem" &&
              lower !== "formulatype" &&
              (!(isCashflowTable || isSocieTable) || (lower !== "issubrow" && lower !== "isnew" && lower !== "isnewrow")) &&
              lower !== "groupingid" && lower !== "grouping id" && (!isCashflowTable || lower !== "formula")
            );
          });
          currentCols = filteredCols.length;
          currentTableName = incoming.tableTitle || "";
        }
      }
    }

    setHoverRows(0);
    setHoverCols(0);
    setTempTableName(currentTableName);
    setTempRows(currentRows);
    setTempCols(currentCols);
    setTempEnableCells(currentEnableCells);
    setIsModalOpen(true);
  };

  const closeRowOptions = () => {
    setHoveredRowIdx(null);
  };

  const toggleRowBold = (r) => {
    if (disabled || r === 0) return;
    
    // Create new row metadata with toggled isBold
    const newMeta = [...rowMetadata];
    if (!newMeta[r - 1]) newMeta[r - 1] = {};
    newMeta[r - 1] = { ...newMeta[r - 1], isBold: !newMeta[r - 1].isBold };
    
    ignoreValueSyncRef.current = true;
    setRowMetadata(newMeta);

    // Provide the toggled row to the parent via an optional callback
    if (onToggleRowBold) {
      onToggleRowBold(r - 1, newMeta[r - 1]);
    }
    
    // Auto-save by triggering onChange with the newly computed payload
    if (onChange) {
      onChange(computePayload(newMeta));
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    onCancel?.();
  };

  useEffect(() => {
    if (forceOpen && !disabled) {
      openModal();
      onAfterOpen?.();
    }
  }, [forceOpen, disabled]);

  const initCells = (rows, cols) => {
    const next = {};
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) next[`${r}-${c}`] = "";
    }
    return next;
  };

  const applyValueToState = (incoming) => {
    if (!incoming) return;

    const cellEntriesEnabled =
      incoming.hasCellData !== undefined
        ? incoming.hasCellData
        : (incoming.tableType === "content" || (Array.isArray(incoming.table) && incoming.table.length > 0));
    setEnableCellEntries(cellEntriesEnabled);

    if (Array.isArray(incoming.table) && incoming.table.length > 0) {
      const rows = incoming.table.length + 1; // header + data
      const columns = Object.keys(incoming.table[0] || {}).filter(c => {
        const lower = String(c.name || c || "").toLowerCase().trim();
        return (
          lower !== "linetype" && 
          lower !== "line type" && 
          lower !== "line-type" && 
          lower !== "_id" && 
          lower !== "createdviaaddlineitem" &&
          lower !== "cashflowid" &&
          lower !== "isedited" &&
          lower !== "isbold" &&
          lower !== "reference" &&
          lower !== "references" &&
          lower !== "lineitem" &&
          lower !== "formulatype" &&
          (!(isCashflowTable || isSocieTable) || (lower !== "issubrow" && lower !== "isnew" && lower !== "isnewrow")) &&
          lower !== "groupingid" && lower !== "grouping id" && (!isCashflowTable || lower !== "formula")
        );
      });
      const cols = columns.length;

      if (!rows || !cols) return;

      const base = initCells(rows, cols);

      columns.forEach((colName, c) => {
        const displayName = (isCashflowTable && colName === "name") ? "Particulars" : colName;
        base[`0-${c}`] = (isDisclosureTable && String(colName).startsWith("column_")) ? "" : displayName;
      });

      incoming.table.forEach((rowObj, r) => {
        columns.forEach((colName, c) => {
          let val = rowObj[colName] ?? "";
          if (isCashflowTable && (colName === "currentYear" || colName === "previousYear") && typeof val === "string" && val === rowObj["name"]) {
            val = "";
          }
          base[`${r + 1}-${c}`] = val;
        });
      });

      setTableName(incoming.tableTitle || "");
      setSelectedRows(rows);
      setSelectedCols(cols);
      setCellValues(base);
      if (incoming.table) {
        setRowMetadata(incoming.table.map(rowObj => ({ 
          ...rowObj,
          // normalize id from multiple possible shapes (direct or nested `data`)
          _id:
            rowObj._id ||
            rowObj.id ||
            rowObj.cashflowID ||
            rowObj.rowID ||
            rowObj.subNoteID ||
            rowObj.data?._id ||
            rowObj.data?.id ||
            rowObj.data?.cashflowID ||
            rowObj.data?.rowID ||
            rowObj.data?.subNoteID,
          isSubRow: rowObj.isSubRow || rowObj.issubrow || rowObj.data?.issubrow || false,
          isNewRow: rowObj.isNewRow || rowObj.isnewrow || rowObj.data?.isnewrow || false,
          isNew: rowObj.isNew || rowObj.isnew || rowObj.data?.isnew || false
        })));
      }
      setGenerated(true);
      return;
    }

    const incomingRowsArr = incoming.tableRow && incoming.tableRow.length > 0 
      ? incoming.tableRow 
      : (isDisclosureTable ? [{ name: "", isNewRow: true, lineType: "line item" }] : []);
    const rows = incomingRowsArr.length + 1;
    const filteredCols = (incoming.tableColumn || []).filter(c => {
      const lower = String(c.name || "").toLowerCase().trim();
      return (
        lower !== "linetype" && 
        lower !== "line type" && 
        lower !== "line-type" &&
        lower !== "isbold" &&
        lower !== "reference" &&
        lower !== "references" &&
        lower !== "lineitem" &&
        lower !== "formulatype" &&
        (!(isCashflowTable || isSocieTable) || (lower !== "issubrow" && lower !== "isnew" && lower !== "isnewrow")) &&
        lower !== "groupingid" && lower !== "grouping id" && (!isCashflowTable || lower !== "formula")
      );
    });
    const cols = filteredCols.length;

    if (rows <= 1 || cols <= 0) {
      setGenerated(false);
      setTableName(incoming.tableTitle || "");
      setSelectedRows(0);
      setSelectedCols(0);
      setCellValues({});
      return;
    }

    const base = initCells(rows, cols);

    filteredCols.forEach((c, i) => {
      const cName = c?.name ?? "";
      const displayName = (isCashflowTable && cName.toLowerCase() === "name") ? "Particulars" : cName;
      base[`0-${i}`] = (isDisclosureTable && String(cName).startsWith("column_")) ? "" : displayName;
    });

    incomingRowsArr.forEach((r, i) => {
      base[`${i + 1}-0`] = r?.name ?? "";
    });

    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(base);
    setTableName(incoming.tableTitle || "");
    setRowMetadata(
      (incomingRowsArr || []).map((rowObj) => ({
        ...rowObj,
        _id:
          rowObj?._id ||
          rowObj?.id ||
          rowObj?.cashflowID ||
          rowObj?.rowID ||
          rowObj?.subNoteID ||
          rowObj?.data?._id ||
          rowObj?.data?.id ||
          rowObj?.data?.cashflowID ||
          rowObj?.data?.rowID ||
          rowObj?.data?.subNoteID,
        isSubRow:
          rowObj?.isSubRow ||
          rowObj?.issubrow ||
          rowObj?.data?.issubrow ||
          false,
        isNewRow:
          rowObj?.isNewRow ||
          rowObj?.isnewrow ||
          rowObj?.data?.isnewrow ||
          false,
        isNew:
          rowObj?.isNew ||
          rowObj?.isnew ||
          rowObj?.data?.isnew ||
          false,
      }))
    );
    setGenerated(true);
  };

  useEffect(() => {
    if (!value) return;

    const hasCellTable = Array.isArray(value.table) && value.table.length > 0;
    const incomingRows = hasCellTable ? value.table.length + 1 : (value.tableRow?.length || 0) + 1;

    const incomingCols = hasCellTable
      ? Object.keys(value.table[0] || {}).filter(c => {
          const lower = String(c.name || c || "").toLowerCase().trim();
          return (
            lower !== "linetype" && 
            lower !== "line type" && 
            lower !== "line-type" && 
            lower !== "_id" && 
            lower !== "createdviaaddlineitem" &&
            lower !== "cashflowid" &&
            lower !== "isedited" &&
            lower !== "isbold" &&
            lower !== "reference" &&
            lower !== "references" &&
            lower !== "lineitem" &&
            lower !== "formulatype" &&
            (!(isCashflowTable || isSocieTable) || (lower !== "issubrow" && lower !== "isnew" && lower !== "isnewrow")) &&
            lower !== "groupingid" && lower !== "grouping id" && (!isCashflowTable || lower !== "formula")
          );
        }).length
      : (value.tableColumn || []).filter(c => {
          const lower = String(c.name || "").toLowerCase().trim();
          return (
            lower !== "linetype" && 
            lower !== "line type" && 
            lower !== "line-type" &&
            lower !== "isbold" &&
            lower !== "reference" &&
            lower !== "references" &&
            lower !== "lineitem" &&
            lower !== "formulatype" &&
            (!(isCashflowTable || isSocieTable) || (lower !== "issubrow" && lower !== "isnew" && lower !== "isnewrow")) &&
            lower !== "groupingid" && lower !== "grouping id" && (!isCashflowTable || lower !== "formula")
          );
        }).length;

    // Check if the incoming value matches the expected state we recently set locally
    if (ignoreValueSyncRef.current) {
      if (incomingRows === selectedRows && incomingCols === selectedCols) {
        // The parent value has caught up with our local dimensions!
        // We can stop ignoring sync now.
        ignoreValueSyncRef.current = false;
      } else {
        // The parent value is still outdated. Skip syncing to avoid overwriting our new local state.
        return;
      }
    }

    // Sync if row or col count changed from outside (e.g., via Add Line Item or external column edits)
    if (generated && (incomingRows !== selectedRows || incomingCols !== selectedCols)) {
      applyValueToState(value);
      return;
    }

    // Initial Load
    if (!generated) {
      const hasSchemaTable = Array.isArray(value.tableRow) && value.tableRow.length > 0;
      if (hasCellTable || hasSchemaTable) {
        applyValueToState(value);
      }
      return;
    }

    // Metadata Sync (Crucial for picking up IDs after auto-save)
    if (Array.isArray(value.table)) {
      const needsSync = 
        value.table.length !== rowMetadata.length ||
        value.table.some((row, i) => (row._id || row.id) !== rowMetadata[i]?._id);

      if (needsSync) {
        setRowMetadata(value.table.map(rowObj => ({
          ...rowObj,
          _id:
            rowObj._id ||
            rowObj.id ||
            rowObj.rowID ||
            rowObj.subNoteID ||
            rowObj.data?._id ||
            rowObj.data?.id ||
            rowObj.data?.rowID ||
            rowObj.data?.subNoteID,
          isSubRow: rowObj.isSubRow || rowObj.issubrow || rowObj.data?.issubrow || false,
          isNewRow: rowObj.isNewRow || rowObj.isnewrow || rowObj.data?.isnewrow || false,
          isNew: rowObj.isNew || rowObj.isnew || rowObj.data?.isnew || false
        })));
      }
    }
  }, [value, generated, selectedRows, selectedCols, rowMetadata]);

  useEffect(() => {
    if (!isModalOpen) {
      modalPrefilledRef.current = false;
    }
  }, [isModalOpen]);

  const handleModalUpdate = () => {
    if (disabled) return;

    const rows = tempRows || hoverRows;
    const cols = tempCols || hoverCols;
    if (!rows || !cols) return;

    ignoreValueSyncRef.current = true;

    const prev = cellValues || {};
    const merged = initCells(rows, cols);

    Object.keys(prev).forEach((k) => {
      const [r, c] = k.split("-").map(Number);
      if (r < rows && c < cols) merged[k] = prev[k];
    });

    setTableName(tempTableName);
    setSelectedRows(rows);
    setSelectedCols(cols);
    setCellValues(merged);
    setEnableCellEntries(tempEnableCells);
    setGenerated(true);
    skipPrefillOnCloseRef.current = true;
    justClosedModalRef.current = true;
    setIsModalOpen(false); // Close without calling onCancel
  };

  const handleCellChange = (r, c, v) => {
    if (disabled) return;
    lastChangedRowRef.current = r;
    setCellValues((prev) => ({ ...prev, [`${r}-${c}`]: v }));
  };

  const computePayload = (overrideRowMeta = null) => {
    const tableColumn = [];
    for (let c = 0; c < selectedCols; c++) {
      tableColumn.push({
        name: String(cellValues[`0-${c}`] ?? "").trim() || `column_${c}`,
        lineType: "line item",
      });
    }

    const tableRow = [];
    for (let r = 1; r < selectedRows; r++) {
      const existingRowMeta = (overrideRowMeta ? overrideRowMeta[r - 1] : rowMetadata[r - 1]) || {};
      tableRow.push({
        ...existingRowMeta,
        name: String(cellValues[`${r}-0`] ?? "").trim(),
        lineType: "line item",
      });
    }

    return {
      tableTitle: tableName ?? "",
      tableRow,
      tableColumn,
    };
  };

  const buildStructuredTable = () => {
    if (
      !selectedRows ||
      !selectedCols ||
      selectedRows < 2 ||
      selectedCols < 1
    ) {
      return [];
    }

    const columnNames = [];
    for (let c = 0; c < selectedCols; c++) {
      columnNames.push(
        String(cellValues[`0-${c}`] ?? "").trim() || `column_${c}`
      );
    }

    const tableData = [];

    for (let r = 1; r < selectedRows; r++) {
      const rowObject = { ...(rowMetadata[r - 1] || {}) };

      for (let c = 0; c < selectedCols; c++) {
        const columnKey = (isCashflowTable && columnNames[c] === "Particulars") ? "name" : (columnNames[c] || `column_${c}`);
        rowObject[columnKey] = String(cellValues[`${r}-${c}`] ?? "").trim();
      }

      tableData.push(rowObject);
    }

    return tableData;
  };

  useEffect(() => {
    if (disabled || !generated || isModalOpen) return;

    const basePayload = computePayload();
    const tableData = buildStructuredTable();

    const finalPayload = {
      ...basePayload,
      tableData,
      hasCellData: enableCellEntries,
    };

    const serialized = JSON.stringify(finalPayload);

    if (lastSentPayloadRef.current === serialized) return;

    lastSentPayloadRef.current = serialized;
    onChange?.(finalPayload, lastChangedRowRef.current);

    if (justClosedModalRef.current) {
      onSaveStructure?.(finalPayload);
      justClosedModalRef.current = false;
    }
  }, [
    disabled,
    generated,
    isModalOpen,
    tableName,
    selectedRows,
    selectedCols,
    cellValues,
    enableCellEntries,
  ]);

  const dragRowIndexRef = useRef(null);


  const getSectionId = (rowIndex) => {
  let section = 0;

  for (let r = 1; r <= rowIndex; r++) {
    const rowLabel = cellValues[`${r}-0`] || "";

    const permissions = getRowPermissions
      ? getRowPermissions(rowLabel)
      : { allowRearrange: true };

    if (permissions.allowRearrange === false) {
      section++;
    }
  }

  return section;
};

  const moveRow = (from, to) => {
    if (from === to) return;

     if (isSocieTable) {
    const sourceSection = getSectionId(from);
    const destinationSection = getSectionId(to);

    if (sourceSection !== destinationSection) {
      return;
    }
  }
  
    ignoreValueSyncRef.current = true;

    setCellValues((prev) => {
      const next = {};
      const rows = selectedRows;
      const cols = selectedCols;

      const order = [...Array(rows).keys()];
      const [moved] = order.splice(from, 1);
      order.splice(to, 0, moved);

      order.forEach((oldR, newR) => {
        for (let c = 0; c < cols; c++) {
          next[`${newR}-${c}`] = prev[`${oldR}-${c}`] ?? "";
        }
      });

      return next;
    });

    setRowMetadata((prev) => {
      const next = [...prev];
      if (next.length > 0) {
        const [moved] = next.splice(from - 1, 1);
        next.splice(to - 1, 0, moved);
      }
      return next;
    });
  };

  const deleteRow = (r) => {
    if (disabled || r === 0) return;

    if (onDeleteRow) {
      onDeleteRow(r, rowMetadata[r - 1]);
      return;
    }
    
    ignoreValueSyncRef.current = true;

    setCellValues((prev) => {
      const next = {};
      const rows = selectedRows;
      const cols = selectedCols;
      let targetRow = 0;
      for (let oldR = 0; oldR < rows; oldR++) {
        if (oldR === r) continue;
        for (let c = 0; c < cols; c++) {
          next[`${targetRow}-${c}`] = prev[`${oldR}-${c}`] ?? "";
        }
        targetRow++;
      }
      return next;
    });
    setSelectedRows((prev) => prev - 1);
  };

  const addRow = () => {
    if (disabled) return;

    if (onAddRow) {
      onAddRow();
      return;
    }

    ignoreValueSyncRef.current = true;

    const newRowIdx = selectedRows;
    setCellValues((prev) => {
      const next = { ...prev };
      next[`${newRowIdx}-0`] = ""; // empty so placeholder shows
      for (let c = 1; c < selectedCols; c++) {
        next[`${newRowIdx}-${c}`] = ""; // empty so placeholder shows
      }
      return next;
    });
    setRowMetadata((prev) => [...prev, { isNew: true }]);
    setSelectedRows((prev) => prev + 1);
  };

  const isNumericColumn = (colIndex) => {
    if (colIndex === 0) return false;

    // 1. Check header keywords
    const headerVal = String(cellValues[`0-${colIndex}`] || "").toLowerCase();
    const keywords = [
      "year", "amount", "shares", "number", "rate", "%", "value", 
      "total", "months", "days", "wans", "check", "count", "weighted",
      "cy", "py"
    ];
    if (keywords.some(k => headerVal.includes(k))) return true;

    // 2. Check data rows for text
    if (selectedRows < 2) return true;
    for (let r = 1; r < selectedRows; r++) {
      const val = String(cellValues[`${r}-${colIndex}`] ?? "").trim();
      if (val === "" || val === "-" || val === "0") continue;
      if (!isNumeric(val) && !/^\([\d,]+\.?\d*\)$/.test(val)) {
        return false;
      }
    }
    return true;
  };

  return (
    <div
      style={{
        margin: !generated ? "0" : (isSocieTable ? "0" : (hideHeader ? "0" : "8px 0")),
        border: !generated ? "none" : (isSocieTable ? "none" : (hideHeader ? "none" : "1px solid #CCD5D3")),
        borderRadius: isSocieTable ? 0 : (hideHeader ? "0 0 12px 12px" : 12),
        paddingTop: !generated ? 0 : ((isDisclosureTable && hideHeader) ? "0px" : (isDisclosureTable ? "25px" : (isSocieTable ? "0" : (hideHeader ? "0" : 12)))),
        paddingBottom: !generated ? 0 : ((isDisclosureTable && hideHeader) ? "0px" : (isDisclosureTable ? "2px" : (isSocieTable ? "0" : (hideHeader ? "0px" : 12)))),
        paddingLeft: !generated ? 0 : ((isDisclosureTable || isSocieTable || isCashflowTable) ? "0" : (hideHeader ? "0" : 12)),
        paddingRight: !generated ? 0 : ((isDisclosureTable || isSocieTable || isCashflowTable) ? "0" : (hideHeader ? "0" : 12)),
        marginTop: !generated ? 0 : (isSocieTable ? "0" : (hideHeader ? "0" : "-33px")),
        backgroundColor: !generated ? "transparent" : (isSocieTable ? "transparent" : "#fff"),
        overflow: !generated ? "visible" : (isSocieTable ? "visible" : "hidden"),
        textTransform: "none",
        fontSize: "14px",
        width: (isDisclosureTable || isSocieTable || isCashflowTable) ? "100%" : "",
        maxWidth: (isDisclosureTable && hideHeader) ? "none" : (isDisclosureTable ? "1230px" : ""),
        height: !generated ? 0 : "auto",
      }}
    >
      {!hideHeader && generated && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            alignItems: "center",
          }}
        >
          <h5
            style={{
              margin: 0,
              color: "#205a46",
              fontSize: "17px",
              padding: isSocieTable ? "6px 16px 6px 28px" : "6px 7px",
            }}
          >
            {tableName || "Untitled Table"}
          </h5>

          {/* ICON COMPLETELY HIDDEN WHEN DISABLED */}
          {!disabled && (
            <div style={{ display: "flex", gap: "10px", alignItems: "center", marginRight: isSocieTable ? "16px" : "0", marginTop: isSocieTable ? "4px" : "0" }}>
              {onDelete && (
                <DelIcon 
                  className={commoncss.clickable}
                  onClick={onDelete}
                  style={{ width: "18px", height: "18px" }}
                />
              )}
              <EditIcon
                className={commoncss.clickable}
                onClick={openModal}
                style={{ width: "20px", height: "20px" }}
              />
            </div>
          )}
        </div>
      )}

      {generated && (
        <div
          className={isSocieTable ? "hide-scrollbar-all" : (isCashflowTable ? "custom-inner-scroll" : "")}
          style={{
            marginTop: hideHeader ? 0 : 12,
            overflowX: "auto",
            overflowY: (isSocieTable || isCashflowTable) ? "auto" : "visible",
            maxHeight: isSocieTable ? "580px" : (isCashflowTable ? "590px" : "none"),
            width: "100%",
            border: ((isDisclosureTable || isCashflowTable) && hideHeader) ? "none" : (hideHeader ? "1px solid #CCD5D3" : "none"),
            borderRadius: (isDisclosureTable && hideHeader) ? 0 : (isSocieTable ? 0 : (hideHeader ? "0px 0px 10px 10px !important" : "10px !important")),
            borderTop: ((isDisclosureTable || isCashflowTable) && hideHeader) ? "none" : (hideHeader ? "1px solid #CCD5D3 !important" : "none !important"),
          }}
        >
          <style>{`
            .hide-scrollbar-all {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            .hide-scrollbar-all::-webkit-scrollbar {
              display: none !important;
            }
            .custom-inner-scroll::-webkit-scrollbar {
              width: 0px !important;
              height: 6px;
            }
            .custom-inner-scroll::-webkit-scrollbar-track {
              background: #f4faf5;
              border-radius: 4px;
            }
            .custom-inner-scroll::-webkit-scrollbar-thumb {
              background: #c2e6d1;
              border-radius: 4px;
            }
            .custom-inner-scroll::-webkit-scrollbar-thumb:hover {
              background: #99aba7;
            }
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .hide-scrollbar {
              scrollbar-width: none;
              -ms-overflow-style: none;
            }
            table.template-table-override tr.header-row td {
              border-bottom: 1px solid #CCD5D3 !important;
              background-color: #eff7f0 !important;
            }
            table.template-table-override th:nth-child(1),
            table.template-table-override td:nth-child(1) {
              padding-left: ${isSocieTable ? '28px' : (isCashflowTable ? '24px' : (isDisclosureTable ? '16px' : (hideHeader ? '18px' : '10px')))} !important;
              padding-right: 20px !important;
            }
            table.template-table-override th:last-child,
            table.template-table-override td:last-child {
              padding-right: ${isSocieTable ? '16px' : '10px'} !important;
            }
            table.template-table-override td, table.template-table-override th {
              border: none !important;
            }
            .template-edit-input:focus {
              border: none !important;
              box-shadow: none !important;
              padding: 0 !important;
            }
            table.template-table-override tr.socie-header-row td {
              padding-top: 14px !important;
              padding-bottom: 14px !important;
            }
          `}</style>
          <table
            className="template-table-override"
            style={{
              borderCollapse: "collapse",
              width: isDisclosureTable ? "100%" : "max-content",
              tableLayout: "fixed",
              minWidth: "100%",
              border: "none",
            }}
          >
            <tbody>
              {[...Array(selectedRows)].map((_, r) => {
                const rowLabel = cellValues[`${r}-0`] || "";
                const permissions = getRowPermissions
                  ? getRowPermissions(rowLabel)
                  : { allowEdit: true, allowDelete: false, allowRearrange: true };

                // Header row defaults
                const allowEditRow = r === 0 ? true : permissions.allowEdit ?? true;
                const allowDeleteRow = r === 0 ? false : permissions.allowDelete ?? true;
                const allowRearrangeRow = r === 0 ? false : (allowReorder && (permissions.allowRearrange ?? true));

                return (
                  <tr
                    key={r}
                    className={`${libcss.socieRow} ${r === 0 ? "header-row" : ""} ${isSocieTable && r === 0 ? "socie-header-row" : ""} ${r === selectedRows - 1 ? "last-row" : ""} ${r === selectedRows - 2 ? "second-last-row" : ""}`}
                    onMouseEnter={() => setHoveredRowIdx(r)}
                    onMouseLeave={() => setHoveredRowIdx(null)}
                    onDragOver={(e) => allowRearrangeRow && e.preventDefault()}
                    onDrop={() => {
                      if (!allowRearrangeRow || dragRowIndexRef.current == null)
                        return;
                      moveRow(dragRowIndexRef.current, r);
                      dragRowIndexRef.current = null;
                    }}
                  >
                  {[...Array(selectedCols)].map((_, c) => {
                    const editable = enableCellEntries || r === 0 || c === 0;

                    const key = `${r}-${c}`;

                    // Determine alignment:
                    const colIsNumeric = isNumericColumn(c);
                    const textAlign = (colIsNumeric && c !== 0) ? "right" : "left";
                    
                    let displayValue = "";
                    if (r === 0) {
                      displayValue = isSocieTable && c === 0 ? "Particulars" : (cellValues[key] ?? "");
                      if (isCashflowTable) {
                        if (displayValue === "currentYear") displayValue = "Current Year";
                        else if (displayValue === "previousYear") displayValue = "Previous Year";
                      }
                    } else {
                      displayValue = focusedCell === key
                        ? cellValues[key] ?? ""
                        : isNumeric(cellValues[key])
                        ? formatIndianNumber(cellValues[key])
                        : cellValues[key] ?? "";
                    }

                    // Pre-calculate isLinked to apply cell-level styling (e.g. bold)
                    let resolvedCol;
                    let isLinkable = false;
                    let isLinked = null;
                    
                    if (isSocieTable) {
                      if (c > 0) {
                        isLinkable = true;
                        resolvedCol = String(cellValues[`0-${c}`] || "").trim();
                      }
                    } else {
                      const colHeaderName = String(cellValues[`0-${c}`] || "").trim().toLowerCase();
                      const colField = String(value?.tableColumn?.[c]?.field || "").trim().toLowerCase();
                      
                      const cyKeywords = ["cy", "current year", "currentyear", "amountcy", "current_year"];
                      const pyKeywords = ["py", "previous year", "previousyear", "amountpy", "previous_year"];
                      
                      let isCY = cyKeywords.includes(colHeaderName) || cyKeywords.includes(colField);
                      let isPY = pyKeywords.includes(colHeaderName) || pyKeywords.includes(colField);
                      
                      const isDateHeader = !isNaN(Date.parse(colHeaderName)) && /\d{4}/.test(colHeaderName);
                      if (isDisclosureTable && !isCY && !isPY && isDateHeader) {
                        if (c === 1) isCY = true;
                        if (c === 2) isPY = true;
                      }

                      if (isCY || isPY) {
                        isLinkable = true;
                        resolvedCol = isCY ? "currentYear" : "previousYear";
                      }
                    }
                    
                    if (isLinkable && resolvedCol) {
                      const rowName = String(cellValues[`${r}-0`] || "").trim() || String(r);
                      const linkKeyAPI = `${tableId}-${rowName}-${resolvedCol}`;
                      const linkKeyLocal = `${tableId}-${r}-${c}`;
                      isLinked = linkedCells && (linkedCells[linkKeyAPI] || linkedCells[linkKeyLocal]);
                    }

                    return (
                      <td
                        key={c}
                        className={`${
                          isSocieTable ? libcss.custTable1 : libcss.custTabletd
                        } ${r === 0 ? libcss.tableHeaderCell : ""}`}
                        onMouseEnter={() => setHoveredCell(`${r}-${c}`)}
                        onMouseLeave={() => setHoveredCell(null)}
                        style={{
                          backgroundColor: r === 0 ? "#eff7f0" : (isSocieTable && c === 0) ? "#fff" : editable ? "#fff" : "none",
                          border: "none",
                          borderTop: "none",
                          position: isSocieTable && (r === 0 || c === 0) ? "sticky" : "relative",
                          top: isSocieTable && r === 0 ? 0 : undefined,
                          left: isSocieTable && c === 0 ? 0 : undefined,
                          zIndex: isSocieTable && r === 0 && c === 0 ? 20 : (isSocieTable && r === 0 ? 10 : (isSocieTable && c === 0 ? 5 : undefined)),
                          padding: isSocieTable ? (c === 0 ? "14px 16px 14px 28px" : "14px 16px") : (hideHeader && c === 0 ? "10px 10px 10px 18px" : undefined),
                          width: isSocieTable
                             ? (c === 0 ? "400px" : "250px")
                             : (isDisclosureTable)
                             ? (c === 0 ? "300px" : "250px")
                             : (c === 0 ? "250px" : (c === selectedCols - 1 && allowDeleteRow ? "210px" : "180px")),
                          minWidth: isSocieTable
                             ? (c === 0 ? "500px" : "300px")
                             : (isDisclosureTable)
                             ? (c === 0 ? "250px" : "250px")
                             : (c === 0 ? "250px" : (c === selectedCols - 1 && allowDeleteRow ? "210px" : "180px")),
                          verticalAlign: r === 0 ? "top" : "middle",
                          borderRadius: "0px !important",
                        }}
                      >
                       {isSocieTable && c === 0 && r > 0 && allowRearrangeRow && (
                          <span
                            draggable
                            onDragStart={() => (dragRowIndexRef.current = r)}
                            style={{
                              position: "absolute",
                              left: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 10,
                              cursor: "grab",
                              color: "#888",
                              userSelect: "none",
                              padding: 0,
                              display: "flex",
                              alignItems: "center",
                              flexShrink: 0,
                              opacity: hoveredRowIdx === r ? 1 : 0,
                              pointerEvents: hoveredRowIdx === r ? "auto" : "none",
                              transition: "opacity 0.2s",
                            }}
                            className={libcss.dragHandle}
                            title="Reorder row"
                          >
                            <img src={MenuIcon} alt="drag" style={{ cursor: "grab", width: 9 }} />
                          </span>
                        )}

                        <div 
                          className={isDisclosureTable ? libcss.disclosureCell : ""}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: textAlign === "right" ? "flex-end" : "flex-start",
                            gap: (isSocieTable && c === 0) ? "0" : (c === 0 ? "8px" : "4px"),
                            width: "100%",
                            height: "100%",
                            position: "relative",
                          }}
                        >
                          {!isSocieTable && c === 0 && r > 0 && (
                            <span
                              draggable
                              onDragStart={() => (dragRowIndexRef.current = r)}
                              style={{
                                position: "static",
                                zIndex: hideHeader ? 10 : "auto",
                                cursor: "grab",
                                color: "#888",
                                userSelect: "none",
                                padding: 0,
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                                opacity: hoveredRowIdx === r ? 1 : 0,
                                pointerEvents: hoveredRowIdx === r ? "auto" : "none",
                                transition: "opacity 0.2s",
                                marginTop: "0",
                              }}
                              className={libcss.dragHandle}
                              title="Reorder row"
                            >
                              <img src={MenuIcon} alt="drag" style={{ cursor: "grab", width: 9, height: 13 }} />
                            </span>
                          )}
                          {!isSocieTable && c === 0 && r === 0 && (
                            <span style={{ width: 9, flexShrink: 0, display: "inline-block" }} />
                          )}

                          {editable ? (
                            r === 0 || c === 0 ? (
                              <textarea
                                className="template-edit-input"
                                value={displayValue}
                                disabled={disabled || !allowEditRow}
                                title={cellValues[key] ?? ""}
                                onFocus={() => setFocusedCell(key)}
                                onBlur={(e) => {
                                  setFocusedCell(null);
                                  if (onCellBlur) onCellBlur(r, c, e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.target.blur();
                                  }
                                }}
                                placeholder="Type Something..."
                                onChange={(e) => handleCellChange(r, c, e.target.value)}
                                rows={isSocieTable && r === 0 ? 3 : 1}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  background: "transparent",
                                  padding: 0,
                                  margin: 0,
                                  cursor: editable ? "text" : "default",
                                  color: r === 0 ? "#205a46" : (isCashflowTable && (displayValue === "" || displayValue === null || displayValue === "0") ? "#b3c0bd" : "#111111"),
                                  fontSize: r === 0 ? "13px" : "14px",
                                  textTransform: "none",
                                  fontWeight: r === 0 ? 900 : ((rowMetadata[r - 1]?.isBold || isLinked?.isBold) ? "700" : "400"),
                                  textAlign: textAlign,
                                  width: "100%",
                                  resize: "none",
                                  fontFamily: "inherit",
                                  whiteSpace: "normal",
                                  overflow: "hidden",
                                  wordBreak: "break-word",
                                  lineHeight: "1.4",
                                }}
                              />
                            ) : (
                              <input
                                className="template-edit-input"
                                type="text"
                                value={displayValue}
                                disabled={disabled || !allowEditRow}
                                title={cellValues[key] ?? ""}
                                onFocus={() => setFocusedCell(key)}
                                onBlur={(e) => {
                                  setFocusedCell(null);
                                  if (onCellBlur) onCellBlur(r, c, e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.target.blur();
                                  }
                                }}
                                placeholder="Type Something..."
                                onChange={(e) => handleCellChange(r, c, e.target.value)}
                                style={{
                                  flex: 1,
                                  border: "none",
                                  outline: "none",
                                  background: "transparent",
                                  padding: 0,
                                  cursor: editable ? "text" : "default",
                                  color: r === 0 ? "#205a46" : (isCashflowTable && (displayValue === "0" || displayValue === "") ? "#b3c0bd" : "#111111"),
                                  fontSize: r === 0 ? "12px" : "14px",
                                  textTransform: "none",
                                  fontWeight: r === 0 ? 900 : ((rowMetadata[r - 1]?.isBold || isLinked?.isBold) ? "700" : "400"),
                                  textAlign: textAlign,
                                  width: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              />
                            )
                          ) : (
                           <span
                              className={libcss.truncateTwolines}
                              title={cellValues[key] || ""}
                              style={{
                                color: "#555",
                                flex: 1,
                                lineHeight: "1.4",
                                cursor: "default",
                                textAlign: textAlign,
                                display: "block",
                                fontWeight: (rowMetadata[r - 1]?.isBold || isLinked?.isBold) ? "700" : "normal",
                                ...(r === 0 || (isSocieTable && c === 0) ? {
                                  whiteSpace: "normal",
                                  overflow: "visible",
                                  textOverflow: "unset",
                                } : {
                                  overflow: "hidden",
                                  textOverflow: c === 0 ? "ellipsis" : "clip",
                                  whiteSpace: "nowrap",
                                }),
                              }}
                            >
                              {isNumeric(cellValues[key])
                                ? formatIndianNumber(cellValues[key])
                                : cellValues[key] || ""}
                            </span>
                          )}

                          {(() => {
                            const isSocie = isSocieTable;
                            if (!isDisclosureTable && !isCashflowTable && !isSocie) return null;
                            if (disabled) return null;

                            // Dummy space for header row to maintain alignment
                            if (r === 0 && isLinkable && resolvedCol) {
                              return <div style={{ width: "24px", marginLeft: "8px", flexShrink: 0, opacity: 0 }} />;
                            }

                            if (r === 0) return null;
                            if (!isLinkable || !resolvedCol) return null;
                            const isHovered = hoveredCell === `${r}-${c}`;

                            const isVisible = isHovered || isLinked;
                            return (
                                 <div
                                   className={libcss.cellLinkIcon}
                                   style={{
                                     position: "relative",
                                     marginLeft: "8px",
                                     opacity: isVisible ? 1 : 0,
                                     pointerEvents: isVisible ? "auto" : "none",
                                     cursor: "pointer",
                                     display: "flex",
                                     alignItems: "center",
                                     justifyContent: "center",
                                     width: "24px",
                                     height: "24px",
                                     borderRadius: "50%",
                                     flexShrink: 0,
                                     background: isLinked ? "transparent" : "#3AB67D",
                                   }}
                                   onClick={(e) => {
                                     e.stopPropagation();
                                     onCellLinkClick && onCellLinkClick(r, c, value.tableRow);
                                   }}
                                   title={isLinked ? "Edit Link" : "Add Link"}
                                 >
                                   {isLinked ? (
                                      (isLinked.type === "formula" || isLinked.terms) ? (
                                       <FormulaIcon style={{ width: 18, height: 18 }} />
                                     ) : (
                                       <img src={linkedIcon} alt="Linked" style={{ width: 18, height: 10 }} />
                                     )
                                   ) : (
                                     <LinkIcon style={{ width: 16, height: 16, color: "#205a46" }} />
                                   )}
                                 </div>
                               );
                           })()}

                          {!isSocieTable && !hideRowBoldIcon && c === selectedCols - 1 && r > 0 && (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                opacity: hoveredRowIdx === r ? 1 : 0,
                                transition: "opacity 0.2s",
                                flexShrink: 0,
                                pointerEvents: hoveredRowIdx === r ? "auto" : "none",
                                gap: "8px",
                                marginLeft: "8px"
                              }}
                            >
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowBold(r);
                                }}
                                style={{
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                title="Toggle Bold"
                              >
                                <img src={BoldIcon} alt="Bold" style={{ width: "18px", height: "18px", opacity: rowMetadata[r - 1]?.isBold ? 1 : 0.5 }} />
                              </span>
                              {allowDeleteRow && (
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteRow(r);
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    color: "#ff4d4f",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                  title="Delete row"
                                >
                                  <DelIcon style={{ width: "18px", height: "18px" }} />
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {isSocieTable && (
                    <td
                      style={{
                        width: "50px",
                        minWidth: "50px",
                        border: "none",
                        verticalAlign: "middle",
                        textAlign: "center",
                        backgroundColor: r === 0 ? "#eff7f0" : "#fff",
                        position: r === 0 ? "sticky" : "relative",
                        top: r === 0 ? 0 : undefined,
                        zIndex: r === 0 ? 10 : undefined,
                      }}
                    >
                      {r > 0 && hoveredRowIdx === r && (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px"
                          }}
                        >
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowBold(r);
                            }}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                            title="Toggle Bold"
                          >
                            <img src={BoldIcon} alt="Bold" style={{ width: "18px", height: "18px", opacity: rowMetadata[r - 1]?.isBold ? 1 : 0.5 }} />
                          </span>
                          {allowDeleteRow && (
                            <span
                              onClick={() => deleteRow(r)}
                              style={{
                                cursor: "pointer",
                                color: "#ff4d4f",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              title="Delete row"
                            >
                              <DelIcon style={{ width: "18px", height: "18px" }} />
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                  )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {allowAddRow && !disabled && (
            <div 
              onClick={addRow}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 16px",
                color: "#2D8861",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                borderTop: "1px solid #ccd5d3",
                backgroundColor: "#fff",
                borderRadius: "0 0 12px 12px",
              }}
            >
              <span style={{ fontSize: "18px", lineHeight: "1", marginTop: "-3px" }}><PlusIcon/></span>
              <span>New Line Item</span>
            </div>
          )}
        </div>
      )}

      {isModalOpen && !disabled && (
        <div
          aria-modal="true"
          role="dialog"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99,
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            style={{
              width: 480,         
              background: "#fff",
              borderRadius: 16,   
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)", 
              display: "flex",
              flexDirection: "column",
              gap: 14,            
              textTransform: "none",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #CCD5D3",
                padding: "14px",
              }}
            >
              <h3
                style={{
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0px",
                  margin: 0,
                }}
              >
                {generated ? "Edit Table" : "Configure Table"}
              </h3>

              <img
                src={closeIcon}
                alt="Close"
                onClick={closeModal}
                style={{ width: 20, height: 20, cursor: "pointer" }}
              />
            </div>

            <div style={{ padding: "10px 16px", }}>
              <label
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#33584e",
                  marginBottom: 6,
                  display: "block",
                }}
              >
                Table name
              </label>

              <input
                name="tableName"
                value={tempTableName}
                placeholder="Type Something..."
                onChange={(e) => setTempTableName(e.target.value)}
                style={{
                  width: "99%",
                  padding: "6px 20px 6px 10px",
                  borderRadius: 10,
                  border: "1px solid #c2e6d1",
                  backgroundColor: "#f4faf5",
                }}
              />
            </div>

            <div style={{ padding: "0 16px 10px 16px", marginTop: 0 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#33584e",
                  cursor: "pointer",
                  "text-transform": "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={tempEnableCells}
                  onChange={(e) => setTempEnableCells(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                Enable inputs for all table cells
              </label>
            </div>

            <div style={{ padding: "0 20px" }}>
             

              <div
                style={{
                  width: "99%",
                  maxwidth: "fit-content",
                  height: 260,
                  border: "1px solid #ccd5d3",
                  borderRadius: 12,
                  padding: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  overflow: "hidden",
                }}
              >
                <div
                  className="hide-scrollbar"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    width: "100%",
                  }}
                >
                  <SizePicker
                    totalRows={totalRows}
                    totalCols={totalCols}
                    hoverRows={hoverRows}
                    hoverCols={hoverCols}
                    selectedRows={tempRows}
                    selectedCols={tempCols}
                    onHover={(r, c) => {
                      setHoverRows(r);
                      setHoverCols(c);
                    }}
                    onPick={(r, c) => {
                      setTempRows(r);
                      setTempCols(c);
                      setHoverRows(r);
                      setHoverCols(c);
                    }}
                  />
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: "11px",
                    color: "#33584e",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  {(tempRows || hoverRows) > 0 &&
                  (tempCols || hoverCols) > 0
                    ? `${tempRows || hoverRows} ROWS × ${
                        tempCols || hoverCols
                      } COLUMNS`
                    : "Select table size"}
                </div>
              </div>
              <label
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "#4D6D64",
                  marginBottom: 6,
                  display: "block",
                  marginTop: "10px",
                }}
              >
                The first rows are headers by default
              </label>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                alignItems: "center",
                padding: "20px",
                borderTop: "1px solid #CCD5D3",
              }}
            >
              <button
                style={{
                  color: "#205a46",
                  backgroundColor: "#fff",
                  border: "1px solid #a0dabc",
                  borderRadius: 10,
                  padding: "6px 16px",
                  cursor: "pointer",
                }}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                style={{
                  backgroundColor: "#205a46",
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "6px 16px",
                  cursor: "pointer",
                }}
                disabled={
                  !(tempRows || hoverRows) || !(tempCols || hoverCols)
                }
                title={
                  !(tempRows || hoverRows) || !(tempCols || hoverCols)
                    ? "Pick a size first"
                    : generated ? "Update table" : "Create table"
                }
                onClick={handleModalUpdate}
              >
                {generated ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SizePicker({
  totalRows,
  totalCols,
  hoverRows,
  hoverCols,
  selectedRows,
  selectedCols,
  onHover,
  onPick,
}) {
  const [isMouseDown, setIsMouseDown] = useState(false);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        userSelect: "none",
      }}
      onMouseLeave={() => !isMouseDown && onHover(0, 0)}
      onMouseDown={() => setIsMouseDown(true)}
      onMouseUp={() => setIsMouseDown(false)}
    >
      {[...Array(totalRows)].map((_, r) => (
        <div key={r} style={{ display: "flex" }}>
          {[...Array(totalCols)].map((_, c) => {
            const rr = r + 1;
            const cc = c + 1;
            const active =
              rr <= (isMouseDown ? (hoverRows || 0) : (selectedRows || hoverRows || 0)) &&
              cc <= (isMouseDown ? (hoverCols || 0) : (selectedCols || hoverCols || 0));
            return (
              <div
                key={c}
                onMouseEnter={() => isMouseDown && onHover(rr, cc)}
                onMouseDown={() => onPick(rr, cc)}
                style={{
                  width: 19,
                  height: 19,
                  margin: 1.5,
                  borderRadius: 4,
                  border: active ? "1px solid #3ab67d" : "1px solid transparent",
                  backgroundColor: active ? "#e4f2e6" : "#e3efe7",
                  cursor: "pointer",
                  transition: "background 0.1s ease",
                }}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}



